/**
 * ChatMessageState → core Message (chat/voice parity #1731, phase 2 Task 2.2).
 *
 * The copied core renders cpv3's `Message` shape; TRF's engine speaks
 * `ChatMessageState`. This is the ONE crossing. Roles fold onto the core's
 * two speakers (the agent wire name is `stevens`, kept for diffability);
 * the whole TRF row rides on `extra` so `renderTrfTurn` can decide which
 * turns are TRF-only without the core learning TRF's roles.
 */
import i18n from 'i18next';
import type { Message } from '@core/chat/types';
import type { ChatMessageState } from '@/types/chat';

/**
 * #2109 R3-B15: the handoff lines the client builds. Each renders from
 * `chat:system.<kind>` at display time (the pack's `systemMessage`), so it
 * follows a language switch and exports in the reader's language.
 */
export type SystemKind = 'handback' | 'trferIdleReleased' | 'reassigned' | 'handoffInitiated' | 'slaBreached' | 'trferJoined';

/** A TRF row carrying the R3-B15 kind. `ChatMessageState` is a shared type, so the fields ride as an intersection. */
export type TrfSystemRow = ChatMessageState & { kind?: SystemKind; params?: Record<string, unknown> };

/**
 * Build a client-side system row. `content` is the ENGLISH line from the same
 * key, so a reader of the raw row (and a pack with no wording for the kind)
 * still has a sentence; the display reads the kind.
 */
export function systemRow(
  base: Pick<ChatMessageState, 'id' | 'timestamp' | 'metadata'>,
  kind: SystemKind,
  params: Record<string, unknown> = {},
): TrfSystemRow {
  const english = (i18n.getFixedT('en') as unknown as (k: string, p?: Record<string, unknown>) => string)(`chat:system.${kind}`, params);
  return { ...base, role: 'system', status: 'complete', content: english, kind, params };
}

const STATUS: Record<ChatMessageState['status'], NonNullable<Message['status']>> = {
  sending: 'sending',
  streaming: 'sending',
  complete: 'sent',
  error: 'error',
};

export function toCoreMessage(m: ChatMessageState): Message {
  return {
    id: m.id,
    content: m.content,
    sender: m.role === 'user' ? 'user' : 'stevens',
    timestamp: new Date(m.timestamp),
    status: STATUS[m.status] ?? 'sent',
    isStreaming: m.status === 'streaming',
    // #1731 / review finding 4: `source` is the one TRF metadata field the
    // core reads directly (voice turns get a speaker name; typed ones don't).
    // Everything else the core needs about a TRF row still travels on `extra`.
    ...(m.metadata?.source ? { metadata: { source: m.metadata.source } } : {}),
    // #2109 R3-B15: the core renders a kind in the reader's language.
    ...((m as TrfSystemRow).kind ? { kind: (m as TrfSystemRow).kind, params: (m as TrfSystemRow).params } : {}),
    extra: m,
  };
}

export function toCoreMessages(ms: ChatMessageState[]): Message[] {
  return ms.map(toCoreMessage);
}

/** The TRF row a core message was made from, if it came through this adapter. */
export function trfRowOf(message: Message): ChatMessageState | undefined {
  const extra = message.extra as ChatMessageState | undefined;
  return extra && typeof extra === 'object' && 'role' in extra ? extra : undefined;
}
